import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authGet, authPost, authPut, authDelete } from '../../utils/auth';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Settings, ExternalLink, Check, X, Info, ChevronUp, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';


const POSManagement = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [features, setFeatures] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  
  // Service dialog
  const [serviceDialog, setServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    website: '',
    country: 'AU',
    logo_url: '',
    features: {}
  });
  
  // Feature dialog
  const [featureDialog, setFeatureDialog] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [featureForm, setFeatureForm] = useState({
    id: '',
    name: '',
    type: 'checkmark',
    options: [],
    tooltip: '',
    order: 0
  });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authGet('/auth/me');
        if (!response.data || response.data.role !== 'admin') {
          navigate('/admin/login');
          return;
        }
        fetchData();
      } catch (error) {
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const [servicesRes, featuresRes] = await Promise.all([
        authGet('/admin/pos-services'),
        authGet('/admin/pos-features')
      ]);
      setServices(servicesRes.data || []);
      setFeatures(featuresRes.data?.features || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // ========== SERVICE MANAGEMENT ==========
  const handleOpenServiceDialog = (service = null) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        website: service.website,
        country: service.country || 'AU',
        logo_url: service.logo_url || '',
        features: service.features || {}
      });
    } else {
      setEditingService(null);
      setServiceForm({ name: '', website: '', country: 'AU', logo_url: '', features: {} });
    }
    setServiceDialog(true);
  };

  const handleSaveService = async () => {
    if (!serviceForm.name.trim()) {
      toast.error('Please enter a service name');
      return;
    }

    try {
      if (editingService) {
        await authPut(`/admin/pos-services/${editingService.id}`, serviceForm);
        toast.success('Service updated');
      } else {
        await authPost('/admin/pos-services', serviceForm);
        toast.success('Service added');
      }
      setServiceDialog(false);
      fetchData();
    } catch (error) {
      console.error('Error saving service:', error);
      toast.error('Failed to save service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    
    try {
      await authDelete(`/admin/pos-services/${serviceId}`);
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('Failed to delete service');
    }
  };

  const handleMoveService = async (serviceId, direction) => {
    const idx = services.findIndex(s => s.id === serviceId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === services.length - 1)) return;
    
    const newServices = [...services];
    const temp = newServices[idx];
    newServices[idx] = newServices[idx + direction];
    newServices[idx + direction] = temp;
    
    // Update orders
    const orders = newServices.map((s, i) => ({ id: s.id, order: i + 1 }));
    
    try {
      await authPost('/admin/pos-services/reorder', { orders });
      setServices(newServices);
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  // ========== FEATURE MANAGEMENT ==========
  const handleOpenFeatureDialog = (feature = null) => {
    if (feature) {
      setEditingFeature(feature);
      setFeatureForm({
        id: feature.id,
        name: feature.name,
        type: feature.type,
        options: feature.options || [],
        tooltip: feature.tooltip || '',
        order: feature.order
      });
    } else {
      setEditingFeature(null);
      const maxOrder = features.length > 0 ? Math.max(...features.map(f => f.order)) : 0;
      setFeatureForm({
        id: '',
        name: '',
        type: 'checkmark',
        options: [],
        tooltip: '',
        order: maxOrder + 1
      });
    }
    setNewOption('');
    setFeatureDialog(true);
  };

  const handleSaveFeature = async () => {
    if (!featureForm.name.trim()) {
      toast.error('Please enter a feature name');
      return;
    }

    let featureId = featureForm.id || featureForm.name.toLowerCase().replace(/[^a-z0-9]/g, '_');

    // Enforce id uniqueness for new features (or when a generated id changed).
    // IDs derived from names can collide ("API Access" vs "API-Access" → "api_access").
    const otherIds = new Set(
      features.filter(f => !editingFeature || f.id !== editingFeature.id).map(f => f.id)
    );
    if (otherIds.has(featureId)) {
      let suffix = 2;
      while (otherIds.has(`${featureId}_${suffix}`)) suffix++;
      featureId = `${featureId}_${suffix}`;
    }

    const updatedFeature = { ...featureForm, id: featureId };

    let updatedFeatures;
    if (editingFeature) {
      updatedFeatures = features.map(f => f.id === editingFeature.id ? updatedFeature : f);
    } else {
      updatedFeatures = [...features, updatedFeature];
    }

    try {
      await authPost('/admin/pos-features', { features: updatedFeatures });
      setFeatures(updatedFeatures);
      toast.success(editingFeature ? 'Feature updated' : 'Feature added');
      setFeatureDialog(false);
    } catch (error) {
      console.error('Error saving feature:', error);
      toast.error('Failed to save feature');
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (!window.confirm('Are you sure you want to delete this feature? It will be removed from all services.')) return;
    
    const updatedFeatures = features.filter(f => f.id !== featureId);

    try {
      await authPost('/admin/pos-features', { features: updatedFeatures });
      setFeatures(updatedFeatures);
      toast.success('Feature deleted');
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error('Failed to delete feature');
    }
  };

  const handleMoveFeature = async (featureId, direction) => {
    const sortedFeatures = [...features].sort((a, b) => a.order - b.order);
    const idx = sortedFeatures.findIndex(f => f.id === featureId);
    if ((direction === -1 && idx === 0) || (direction === 1 && idx === sortedFeatures.length - 1)) return;
    
    const newFeatures = [...sortedFeatures];
    const temp = newFeatures[idx];
    newFeatures[idx] = newFeatures[idx + direction];
    newFeatures[idx + direction] = temp;
    
    // Update orders
    const reorderedFeatures = newFeatures.map((f, i) => ({ ...f, order: i + 1 }));
    
    try {
      await authPost('/admin/pos-features', { features: reorderedFeatures });
      setFeatures(reorderedFeatures);
    } catch (error) {
      console.error('Error reordering features:', error);
    }
  };

  const addOption = () => {
    if (!newOption.trim()) return;
    setFeatureForm(prev => ({
      ...prev,
      options: [...prev.options, newOption.trim()]
    }));
    setNewOption('');
  };

  const removeOption = (idx) => {
    setFeatureForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== idx)
    }));
  };

  // Helper to render feature value for service
  const renderFeatureValue = (service, feature) => {
    const value = service.features?.[feature.id];
    
    if (feature.type === 'checkmark') {
      return value ? <Check className="w-4 h-4 text-green-600" /> : <span className="text-muted-foreground">-</span>;
    }
    
    if (feature.type === 'multi-select') {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {value.map((v, i) => (
            <span key={i} className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{v}</span>
          ))}
        </div>
      );
    }
    
    return value || <span className="text-muted-foreground">-</span>;
  };

  // Helper to update service feature value in form
  const updateFeatureValue = (featureId, featureType, value) => {
    setServiceForm(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [featureId]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="p-2 hover:bg-muted rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">POS Management</h1>
              <p className="text-sm text-muted-foreground">Manage point-of-sale services and features</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleOpenServiceDialog()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Service
            </button>
          </div>
        </div>

        {/* Features Summary - Improved UX */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-heading font-semibold">Feature Definitions</h2>
              <p className="text-xs text-muted-foreground">Define the comparison criteria shown in the table</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => handleOpenFeatureDialog()}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add New Feature
            </Button>
          </div>
          
          {features.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-4">No features defined yet. Add features to compare POS services.</p>
          ) : (
            <div className="border rounded-lg divide-y">
              {[...features].sort((a, b) => a.order - b.order).map((f, idx) => (
                <div 
                  key={f.id} 
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/50 group"
                >
                  {/* Order controls */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => handleMoveFeature(f.id, -1)}
                      disabled={idx === 0}
                      aria-label="Move feature up"
                      className="p-0.5 hover:bg-accent rounded disabled:opacity-30"
                    >
                      <ChevronUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleMoveFeature(f.id, 1)}
                      disabled={idx === features.length - 1}
                      aria-label="Move feature down"
                      className="p-0.5 hover:bg-accent rounded disabled:opacity-30"
                    >
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  </div>
                  
                  {/* Feature info */}
                  <div className="flex-1 flex items-center gap-3 cursor-pointer" onClick={() => handleOpenFeatureDialog(f)}>
                    <span className="font-medium text-sm">{f.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      f.type === 'checkmark' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                      f.type === 'dropdown' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {f.type}
                    </span>
                    {f.type !== 'checkmark' && f.options?.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {f.options.length} option{f.options.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {f.tooltip && (
                      <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={f.tooltip}>
                        {f.tooltip}
                      </span>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleOpenFeatureDialog(f)}
                      className="p-1.5 hover:bg-accent rounded"
                      title="Edit feature"
                      aria-label="Edit feature"
                    >
                      <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => handleDeleteFeature(f.id)}
                      className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                      title="Delete feature"
                      aria-label="Delete feature"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Services Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase w-8">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Service</th>
                  {[...features].sort((a, b) => a.order - b.order).map(f => (
                    <th key={f.id} className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase" title={f.tooltip}>
                      <div className="flex items-center gap-1">
                        {f.name}
                        {f.tooltip && <Info className="w-3 h-3 text-muted-foreground" />}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {services.length === 0 ? (
                  <tr>
                    <td colSpan={features.length + 3} className="px-4 py-8 text-center text-muted-foreground">
                      No POS services added yet. Click "Add Service" to get started.
                    </td>
                  </tr>
                ) : (
                  services.map((service, idx) => (
                    <tr key={service.id} className="hover:bg-muted/50">
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-center gap-1">
                          <button
                            onClick={() => handleMoveService(service.id, -1)}
                            disabled={idx === 0}
                            aria-label="Move service up"
                            className="p-0.5 hover:bg-accent rounded disabled:opacity-30"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <span className="text-xs text-muted-foreground">{idx + 1}</span>
                          <button
                            onClick={() => handleMoveService(service.id, 1)}
                            disabled={idx === services.length - 1}
                            aria-label="Move service down"
                            className="p-0.5 hover:bg-accent rounded disabled:opacity-30"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {service.logo_path && (
                            <img 
                              src={`${service.logo_path.replace('/uploads/', '/api/uploads/')}`} 
                              alt={service.name} 
                              className="w-6 h-6 object-contain rounded"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <div>
                            <span className="font-medium">{service.name}</span>
                            {service.website && (
                              <a 
                                href={service.website.startsWith('http') ? service.website : `https://${service.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline flex items-center gap-0.5"
                              >
                                {service.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                                <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            )}
                          </div>
                        </div>
                      </td>
                      {[...features].sort((a, b) => a.order - b.order).map(f => (
                        <td key={f.id} className="px-4 py-3">
                          {renderFeatureValue(service, f)}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => handleOpenServiceDialog(service)}
                            className="p-1.5 hover:bg-accent rounded transition-colors"
                            title="Edit"
                            aria-label="Edit service"
                          >
                            <Pencil className="w-4 h-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={() => handleDeleteService(service.id)}
                            className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                            title="Delete"
                            aria-label="Delete service"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Service Dialog */}
        <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add Service'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Service Name *</Label>
                <Input
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="e.g., Bitaroo"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={serviceForm.website}
                  onChange={(e) => setServiceForm({ ...serviceForm, website: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label>Country</Label>
                <Input
                  value={serviceForm.country}
                  onChange={(e) => setServiceForm({ ...serviceForm, country: e.target.value })}
                  placeholder="AU"
                />
              </div>

              <div className="space-y-2">
                <Label>Logo URL (enter URL to download and store locally)</Label>
                <Input
                  value={serviceForm.logo_url}
                  onChange={(e) => setServiceForm({ ...serviceForm, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                />
                <p className="text-xs text-muted-foreground">
                  Enter a URL to download the logo. URL will be cleared after saving.
                </p>
                {editingService?.logo_path && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs text-muted-foreground">Current logo:</span>
                    <img 
                      src={`${editingService.logo_path.replace('/uploads/', '/api/uploads/')}`}
                      alt="Current logo" 
                      className="w-8 h-8 object-contain rounded border border-border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
              </div>
              
              <div className="border-t border-border pt-4">
                <Label className="mb-3 block">Features</Label>
                <div className="space-y-4">
                  {features.map(feature => (
                    <div key={feature.id} className="space-y-1">
                      <Label className="text-sm flex items-center gap-1">
                        {feature.name}
                        {feature.tooltip && (
                          <span className="text-xs text-muted-foreground" title={feature.tooltip}>
                            <Info className="w-3 h-3 inline" />
                          </span>
                        )}
                      </Label>
                      
                      {feature.type === 'checkmark' && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={serviceForm.features[feature.id] || false}
                            onCheckedChange={(checked) => updateFeatureValue(feature.id, feature.type, checked)}
                          />
                          <label className="text-sm">Yes</label>
                        </div>
                      )}
                      
                      {feature.type === 'dropdown' && (
                        <Select 
                          value={serviceForm.features[feature.id] || ''} 
                          onValueChange={(v) => updateFeatureValue(feature.id, feature.type, v)}
                        >
                          <SelectTrigger className="bg-background">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {feature.options.map(opt => (
                              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {feature.type === 'multi-select' && (
                        <div className="flex flex-wrap gap-2">
                          {feature.options.map(opt => (
                            <label key={opt} className="flex items-center space-x-1">
                              <Checkbox
                                checked={(serviceForm.features[feature.id] || []).includes(opt)}
                                onCheckedChange={(checked) => {
                                  const current = serviceForm.features[feature.id] || [];
                                  const updated = checked 
                                    ? [...current, opt]
                                    : current.filter(v => v !== opt);
                                  updateFeatureValue(feature.id, feature.type, updated);
                                }}
                              />
                              <span className="text-sm">{opt}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <button onClick={() => setServiceDialog(false)} className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">
                  Cancel
                </button>
                <button onClick={handleSaveService} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors">
                  {editingService ? 'Save Changes' : 'Add Service'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Feature Dialog */}
        <Dialog open={featureDialog} onOpenChange={setFeatureDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingFeature ? 'Edit Feature' : 'Add Feature'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Feature Name *</Label>
                <Input
                  value={featureForm.name}
                  onChange={(e) => setFeatureForm({ ...featureForm, name: e.target.value })}
                  placeholder="e.g., API Access"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Feature Type</Label>
                <Select 
                  value={featureForm.type} 
                  onValueChange={(v) => setFeatureForm({ ...featureForm, type: v })}
                >
                  <SelectTrigger className="bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkmark">Checkmark (Yes/-)</SelectItem>
                    <SelectItem value="dropdown">Dropdown (Single Select)</SelectItem>
                    <SelectItem value="multi-select">Multi-Select</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(featureForm.type === 'dropdown' || featureForm.type === 'multi-select') && (
                <div className="space-y-2">
                  <Label>Options</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add option..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                    />
                    <button onClick={addOption} className="px-3 py-2 bg-muted rounded hover:bg-accent transition-colors">
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {featureForm.options.map((opt, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                        {opt}
                        <button onClick={() => removeOption(idx)} className="hover:text-red-500">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Tooltip Description</Label>
                <Textarea
                  value={featureForm.tooltip}
                  onChange={(e) => setFeatureForm({ ...featureForm, tooltip: e.target.value })}
                  placeholder="Describe what this feature means..."
                  rows={2}
                />
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t border-border">
                <button onClick={() => setFeatureDialog(false)} className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors">
                  Cancel
                </button>
                <button onClick={handleSaveFeature} className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors">
                  {editingFeature ? 'Save Changes' : 'Add Feature'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default POSManagement;
